<template>
  <div>
    <h3 class="payment-details__title">Adding payment details</h3>

    <div
      v-if="!hidePriceBreakdown"
      class="payment-details"
    >
      <div class="payment-details__header">
        <div>Charge</div>
        <div>Amount ($)</div>
      </div>

      <div
        class="payment-details__row"
        v-for="session in selectedSessionsWithoutZeros"
        :key="'pc_session_'+ session.id"
      >
        <div>{{ session.name }}</div>
        <div>{{ session.price.toFixed(2) }}</div>
      </div>

      <div
        class="payment-details__row"
        v-for="group in groups"
        :key="'pc_group_'+ group.id"
      >
        <div>{{ group.name }}</div>
        <div>{{ group.price.toFixed(2) }}</div>
      </div>

      <!-- Per-discount lines -->
      <div
        v-for="(d, i) in discountsWithAmounts"
        :key="'pc_discount_' + i"
        class="payment-details__row"
      >
        <div>{{ d.label }}</div>
        <div>- {{ d.amount.toFixed(2) }}</div>
      </div>

      <div
        class="payment-details__row"
        v-for="product in products"
        :key="'pc_product_'+ product.id"
      >
        <div>{{ product.product_name }}</div>
        <div>{{ (product.amount * (product.quantity || 1)).toFixed(2) }}</div>
      </div>

      <div
        v-if="additionalCharge > 0"
        class="payment-details__row"
      >
        <div>{{ additionalChargeName }}</div>
        <div>{{ additionalCharge.toFixed(2) }}</div>
      </div>

      <div
        v-if="paidAmount > 0"
        class="payment-details__row"
      >
        <div>Paid</div>
        <div>- {{ paidAmount.toFixed(2) }}</div>
      </div>

      <!-- Payment fee line items -->
      <div
        v-for="fee in applicableFees"
        :key="'pc_fee_' + fee.id"
        class="payment-details__row payment-details__row--fee"
      >
        <div>
          {{ fee.name }}
          <span class="payment-details__fee-hint">
            ({{ fee.type === 'percentage' ? fee.value + '%' : '$' + fee.value }})
          </span>
        </div>
        <div>{{ fee.calculatedAmount.toFixed(2) }}</div>
      </div>

      <div class="payment-details__total breakdown">
        <div>Total</div>
        <div>{{ totalAmountForPay.toFixed(2) }}</div>
      </div>
    </div>

    <div
      v-else
      class="payment-details"
    >
      <div class="payment-details__total">
        <div>Total</div>
        <div>{{ totalAmountForPay.toFixed(2) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    sessions: Array,
    groups: Array,
    products: Array,
    paidAmount: Number,
    hidePriceBreakdown: Boolean,
    discounts: {
      type: Array,
      default: () => [],
    },
    additionalCharge: Number,
    additionalChargeName: String,
    totalAmountForPay: Number,
    applicableFees: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    selectedSessionsWithoutZeros() {
      return this.sessions.filter(session => session.price != 0)
    },
    discountableSum() {
      let sum = 0
      this.sessions.forEach(s => { sum += s.price })
      this.groups.forEach(g => { sum += g.price })
      return sum
    },
    /**
     * Compute a display amount for each individual discount entry.
     *
     * Dollar discounts are distributed proportionally (each one as a share
     * of the discountable sum), and percent discounts are applied in sequence
     * to the running post-dollar balance — matching the backend logic in
     * ChargeService::buildDiscountSqlExpression.
     */
    discountsWithAmounts() {
      const result = []
      if (!this.discounts || !this.discounts.length) return result

      let runningBase = this.discountableSum

      // Collect all dollar discounts first, then percent discounts
      const dollarEntries  = this.discounts.filter(d => d.priceType === 'Dollar discount')
      const percentEntries = this.discounts.filter(d => d.priceType === 'Percent discount')

      dollarEntries.forEach(d => {
        const amount = Math.min(Number(d.dollarDiscount) || 0, runningBase)
        if (amount > 0 || d.discountName) {
          result.push({ label: d.discountName || 'Discount', amount })
          runningBase -= amount
        }
      })

      percentEntries.forEach(d => {
        const pct    = (Number(d.percentDiscount) || 0) / 100
        const amount = runningBase * pct
        if (amount > 0 || d.discountName) {
          result.push({ label: d.discountName || 'Discount', amount })
          runningBase -= amount
        }
      })

      return result
    },
  },
}
</script>

<style scoped>
.payment-details__fee-hint {
  font-size: 0.85em;
  color: #888;
  margin-left: 4px;
}
</style>

